import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Link,
  Modal,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import CloseIcon from "@mui/icons-material/Close";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { Donut } from "./Donut";
import {
  DataGrid,
  GridCallbackDetails,
  GridCellParams,
  GridEnrichedColDef,
  GridSortModel,
} from "@mui/x-data-grid";
import axios from "../../../../../services/axios";
import { ReactComponent as AccuracyCheck } from "../../../../../assets/IconAccuracyCheck.svg";
import { ReactComponent as AccuracyCross } from "../../../../../assets/IconAccuracyCross.svg";
import { InsightsProgressFilterContext } from "../../../../../context/InsightsProgressFilterContext";
import { ClassExtended, InsightsCtx, InsightsFilterCtx } from "../../../../../interfaces";
import { useHistory, useParams } from "react-router-dom";
import { InsightsContext } from "../../../../../context/InsightsContext";
import { generateGtmEvent, sanitizeData } from "../../../../../Helpers";
import { useXLSXGenerator } from "../../../../../hooks/useXLSXGenerator";
import { CourseMetadata } from "@backend-types";

const useStyles = makeStyles((theme) => ({
  studentModal: {
    position: "absolute",
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  modalTitle: {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: "20px",
  },
  closeButton: {
    color: theme.palette.text.secondary,
  },
  dataTable: {
    borderRadius: "8px",
    border: `2px solid ${theme.palette.divider}`,
    backgroundColor: "white",
    height: "410px",
    "& .MuiDataGrid-columnHeaderTitleContainer": {
      flexDirection: "row !important",
      justifyContent: "left",
    },
    "& .MuiDataGrid-iconButtonContainer": {
      marginLeft: "auto",
    },
    "& .MuiDataGrid-columnHeaders": {
      height: "50px",
      minHeight: "unset",
      borderBottom: `2px solid ${theme.palette.divider}`,
      // border: "3px solid #E8E8E8 !important",
    },
    "& .MuiDataGrid-columnHeader": {
      "&:focus, &:focus-within": {
        outline: "none",
      },
    },
    "& .MuiDataGrid-columnHeader--sorted": {
      color: theme.palette.primary.main,
      "& .MuiDataGrid-sortIcon": {
        color: theme.palette.primary.main,
      },
    },
    "& .MuiDataGrid-cell": {
      border: "0px",
      "&:focus, &:focus-within": {
        outline: "none",
      },
    },
  },
  dataTableHeaderWithBorder: {
    borderRight: `2px solid ${theme.palette.divider}`,
    alignItems: "center",
    "& > .MuiDataGrid-columnSeparator": {
      visibility: "hidden",
    },
  },
  dataTableCellWithBorder: {
    fontWeight: theme.typography.fontWeightMedium,
    borderRight: `2px solid ${theme.palette.divider} !important`,
  },
  dataTableHeader: {
    "& > .MuiDataGrid-columnSeparator": {
      visibility: "hidden",
    },
  },
  sortedDataTableValue: {
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.primary.main,
  },
}));

interface StudentDetailedModalProps {
  open: boolean;
  handleClose: () => void;
  studentName: string;
  progress: number;

  studentId: string;
  studentEmail: string;
  classId: string | number;
  defaultModuleIds: string[];
  classDetails?: ClassExtended;
  courseMetadata?: CourseMetadata;
}

export interface StudentDetails {
  targetId: string;
  targetName: string;
  moduleId: string;
  moduleName: string;
  questionId: string;
  questionName: string;
  questionType: string;
  points: number;
  maxPoints: number;
  attempts: number;
  skillsTriggeredCount: number;
  skillsTriggeredNames: string;
  feedbackHints: number;
  feedbackErrors: number;
}

interface ColumnWidths {
  // key is column name
  [key: string]: number;
}

export const StudentDetailedModal = (props: StudentDetailedModalProps) => {
  const {
    open,
    handleClose,
    progress,
    studentName,
    studentEmail,
    studentId,
    classId,
    classDetails,
    courseMetadata,
  } = props;
  const classes = useStyles();
  const history = useHistory();
  const horiBreakpointSm = useMediaQuery("(max-width:769px)");
  const vertBreakpointSm = useMediaQuery("(max-height:755px)");
  const [studentDetails, setStudentDetails] = React.useState<StudentDetails[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [columnWidths, setColumnWidths] = useState<ColumnWidths>();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const pathParams = useParams<{ class_id: string }>();

  const insightsCtx = useContext(InsightsContext) as InsightsCtx;

  const insightsProgressFilterCtx = useContext(
    InsightsProgressFilterContext
  ) as InsightsFilterCtx;

  const className = classDetails
    ? `${classDetails.subject} ${classDetails.period_block}`
    : `Class ${classId}`;

  const generateXLSX = useXLSXGenerator();
  const handleClickDownload = () => {
    const modifiedDetails = studentDetails.map((item) => {
      const data: any = {
        Problem: item.questionName,
        Target: item.targetName,
        Module: item.moduleName,
        Accuracy: `${item.points ?? 0}/10`,
        Attempts: item.attempts,
      };
      if (!courseMetadata?.insights_page?.disable_feedback_data) {
        data["Skill videos triggered"] = item.skillsTriggeredNames;
        data["Errors"] = item.feedbackErrors;
        data["Hints"] = item.feedbackHints;
      }
      return sanitizeData(data);
    });

    generateXLSX({
      fileName: `${className} - ${studentName} - Problem Details`,
      sheets: [
        {
          name: "Problem Details",
          header: ["heading1"],
          data: modifiedDetails,
        },
      ],
    });
  };

  const displayTextWidth = useCallback((text: string, font: string, fontSize: string) => {
    let canvas = document.createElement("canvas");
    let context = canvas.getContext("2d");
    if (context) {
      context.font = `${fontSize} ${font}`;
      let metrics = context.measureText(text);
      return metrics.width + 50;
    } else {
      return 0;
    }
  }, []);

  const calculateWidths = useCallback(
    (data: any[], page: number, pageSize: number) => {
      const newWidths: ColumnWidths = {
        targetName: 0,
        moduleName: 0,
        skillsTriggeredNames: 0,
      };

      Object.keys(newWidths).forEach((key) => {
        const slice = data.slice(page * pageSize, page * pageSize + pageSize);
        let longestString = "";
        if (slice.length > 0) {
          longestString = slice.reduce((a: any, b: any) => {
            return a.length > b[key]?.length || 0 ? a : b[key];
          });
        }
        newWidths[key] = displayTextWidth(longestString ?? "", "Roboto", "14px");
      });

      setColumnWidths(newWidths);
    },
    [displayTextWidth]
  );

  useEffect(() => {
    const getStudentDetails = async () => {
      let courseFilter = insightsProgressFilterCtx.selectedCourseFilter ?? {
        moduleIds: props.defaultModuleIds,
      };
      if (!courseFilter) return;
      setLoading(true);
      let data = {
        student: { id: studentId, email: studentEmail },
        courseFilter: courseFilter,
        classId: Number(classId),
        v2Feedback: courseMetadata?.insights_page?.use_user_events_v2 ?? false,
      };
      try {
        let response = await axios.post("/insights/student-details", data);
        setStudentDetails(response.data);
        calculateWidths(response.data, 0, pageSize);
        setLoading(false);
      } catch (e) {
        console.log(e);
      }
    };

    if (open) {
      setPage(0);
      getStudentDetails();
    }
    return () => {
      setStudentDetails([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    studentId,
    studentEmail,
    insightsProgressFilterCtx.selectedCourseFilter,
    calculateWidths,
  ]);

  const filterTargetNames = insightsProgressFilterCtx.filters
    .filter((filter: any) => filter.type === "target")
    .map((filter: any) => filter.name);
  const filterModuleNames = insightsProgressFilterCtx.filters
    .filter((filter: any) => filter.type === "module")
    .map((filter: any) => filter.name);

  const columns: GridEnrichedColDef<StudentDetails, any, any>[] = [
    {
      field: "questionName",
      headerName: "Problem",
      type: "string",
      align: "left",
      headerClassName: classes.dataTableHeaderWithBorder,
      cellClassName: classes.dataTableCellWithBorder,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "targetName",
      headerName: "Target",
      align: "left",
      minWidth: columnWidths ? columnWidths["targetName"] : 150,
      sortable: true,
    },
    {
      field: "moduleName",
      headerName: "Module",
      align: "left",
      minWidth: columnWidths ? columnWidths["moduleName"] : 200,
      sortable: true,
    },
    {
      field: "points",
      headerName: "Accuracy",
      align: "left",
      minWidth: 100,
      sortable: true,
      renderCell: (params: GridCellParams) => {
        return (
          params.row.attempts > 0 && (
            <Stack direction="row" alignItems="center" justifyContent="center">
              {params.row.points === 10 ? <AccuracyCheck /> : <AccuracyCross />}
              <Typography variant="body1" sx={{ marginLeft: "12px" }}>
                {params.row.points + "/" + 10}
              </Typography>
            </Stack>
          )
        );
      },
    },
    {
      field: "attempts",
      headerName: "Attempts",
      align: "left",
      minWidth: 100,
      sortable: true,
    },
  ];

  if (!courseMetadata?.insights_page?.disable_feedback_data) {
    columns.push(
      {
        field: "skillsTriggeredNames",
        headerName: "Skill videos triggered",
        align: "left",
        minWidth: 150,
        width: columnWidths ? columnWidths["skillsTriggeredNames"] : 150,
        sortable: true,
      },
      {
        field: "feedbackErrors",
        headerName: "Errors",
        align: "left",
        minWidth: 100,
        sortable: true,
      },
      {
        field: "feedbackHints",
        headerName: "Hints",
        align: "left",
        minWidth: 100,
        sortable: true,
      }
    );
  }

  return (
    <>
      <Modal open={open} onClose={handleClose} aria-labelledby={"student-detailed-modal"}>
        <Card
          sx={{ width: horiBreakpointSm ? "600px" : "900px" }}
          className={classes.studentModal}>
          <CardHeader
            sx={{ padding: "16px 24px" }}
            title={
              <Typography
                id="insights-student-detail-modal-title"
                variant="h6"
                className={classes.modalTitle}>
                {studentName}
              </Typography>
            }
            action={
              <Tooltip
                placement="bottom-end"
                title="Close"
                arrow
                role="tooltip"
                describeChild>
                <IconButton
                  onClick={() => {
                    generateGtmEvent({
                      event: "modal",
                      action: "close",
                      name: "onclick",
                      type: "click",
                      region: "main content",
                      section: `${studentName}`,
                      text: "close cross",
                    });
                    props.handleClose();
                  }}
                  aria-label="close student detailed modal"
                  className={classes.closeButton}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            }
          />
          <Divider />
          <CardContent
            sx={{
              maxHeight: vertBreakpointSm ? "500px" : "700px",
              padding: "24px 32px 32px 32px !important",
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "24px" }}>
                <Donut
                  value={Math.round(progress * 100)}
                  label={"Complete"}
                  aria-label={"overall-progress"}
                />
                <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <Typography
                    sx={{
                      color: "#484848",
                      font: "normal normal 600 18px/24px proxima-soft",
                    }}>
                    Overall Progress
                  </Typography>
                  <Link
                    onClick={() => {
                      const classId = !Number.isNaN(Number(pathParams.class_id))
                        ? Number(pathParams.class_id)
                        : undefined;
                      if (!classId) return;
                      const pathTo = insightsCtx.getStudentUrl(studentId, classId);
                      history.push({
                        pathname: pathTo,
                      });
                      generateGtmEvent({
                        event: "link",
                        action: "click",
                        name: "onclick",
                        type: "internal link",
                        region: "main content",
                        section: "overall progress",
                        text: "view details",
                      });
                      //insightsProgressFilterCtx.setSummaryBackOverride(true);
                      props.handleClose();
                    }}
                    variant="body1">
                    View details
                  </Link>
                  <Link
                    onClick={handleClickDownload}
                    variant="body1"
                    style={{ textDecoration: "none" }}>
                    <span style={{ textDecoration: "underline" }}>Download</span>{" "}
                    <i className="fa-solid fa-download"></i>
                  </Link>
                </Box>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <Typography
                  variant={"body2"}
                  component={"span"}
                  textTransform="uppercase">
                  Module
                </Typography>
                <Typography variant={"body1"} component={"span"}>
                  {filterModuleNames.length > 0
                    ? filterModuleNames.join(", ")
                    : "Displaying data for all modules. Use filter button on previous screen to filter by module."}
                </Typography>
                <Divider />
                <Typography
                  variant={"body2"}
                  component={"span"}
                  textTransform="uppercase">
                  Learning Target
                </Typography>
                <Typography variant={"body1"} component={"span"}>
                  {filterTargetNames.length > 0
                    ? filterTargetNames.join(", ")
                    : "Displaying data for all learning targets. Use filter button on previous screen to filter by learning target."}
                </Typography>
              </Box>
            </Box>
            <Box style={{ width: "100%", marginTop: "16px" }}>
              <DataGrid
                getRowId={(row: any) => row.questionId}
                className={classes.dataTable}
                rows={studentDetails}
                columns={columns}
                disableSelectionOnClick
                disableColumnSelector
                disableDensitySelector
                disableColumnMenu
                hideFooter={studentDetails.length > pageSize ? undefined : true}
                pagination={studentDetails.length > pageSize ? true : undefined}
                pageSize={pageSize}
                onPageSizeChange={(pageSize: number, details: GridCallbackDetails) => {
                  setPageSize(pageSize);
                  setPage(0);
                  calculateWidths(studentDetails, 0, pageSize);
                }}
                page={page}
                onPageChange={(page: number, details: GridCallbackDetails) => {
                  setPage(page);
                  calculateWidths(studentDetails, page, pageSize);
                }}
                onSortModelChange={(model: GridSortModel) => {
                  const colDef = columns.find((item) => item.field === model[0].field);
                  const headerName = colDef?.headerName;
                  generateGtmEvent({
                    event: "link",
                    action: "click",
                    name: "onclick",
                    type: "internal link",
                    region: "main content",
                    section: "student overall progress",
                    text: `${headerName ?? model[0].field} ^ sort by`,
                  });
                }}
                loading={loading}
              />
            </Box>
          </CardContent>
        </Card>
      </Modal>
    </>
  );
};
